import type { AeAdminCompletionDigestFrequency, AeCampaign, AePresentationStyle, AeRespondentConfig, CreateCampaignInput } from '@/types/assessmentEngine';
import { AE_DEFAULT_RESPONDENT_CONFIG } from '@/types/assessmentEngine';
import { campaignResponsePeriodMode, campaignPeriodGranularity } from '@/components/assessment-engine/campaignResponseMode';
import type { TemplateRespondentFramingDefaults } from '@/components/assessment-engine/templateRespondentFraming';
import type { CampaignMessagingSetupValue } from './CampaignInviteSetupFields';
import { defaultCampaignReminderMessage } from './CampaignInviteSetupFields';

export type PeriodGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface CampaignSetupFormState {
  form: CreateCampaignInput;
  responsesPerPeriod: boolean;
  periodGranularity: PeriodGranularity;
  remindOnNewPeriod: boolean;
  messagingSetup: CampaignMessagingSetupValue;
  pendingInviteEmails: string;
  /** Applies to all campaign admins while the campaign is open. */
  adminCompletionDigest: AeAdminCompletionDigestFrequency;
  /** Preserved across saves so digest watermark is not wiped. */
  adminCompletionDigestLastSentAt: string | null;
}

function parseAdminCompletionDigest(
  notificationConfig: Record<string, unknown>
): {
  frequency: AeAdminCompletionDigestFrequency;
  last_sent_at: string | null;
} {
  const raw = notificationConfig.admin_completion_digest;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { frequency: 'every', last_sent_at: null };
  }
  const obj = raw as Record<string, unknown>;
  const freq = obj.frequency;
  const frequency: AeAdminCompletionDigestFrequency =
    freq === 'daily' || freq === 'weekly' || freq === 'monthly' || freq === 'every'
      ? freq
      : 'every';
  const last =
    typeof obj.last_sent_at === 'string' && obj.last_sent_at.trim()
      ? obj.last_sent_at.trim()
      : null;
  return { frequency, last_sent_at: last };
}

function parseRespondentFramingOverride(
  notificationConfig: Record<string, unknown>
): Pick<CampaignMessagingSetupValue, 'opening_purpose' | 'opening_instructions' | 'closing_message'> {
  const raw = notificationConfig.respondent_framing;
  if (!raw || typeof raw !== 'object') {
    return { opening_purpose: '', opening_instructions: '', closing_message: '' };
  }
  const rf = raw as Record<string, unknown>;
  const opening = rf.opening;
  const closing = rf.closing;
  let opening_purpose = '';
  let opening_instructions = '';
  let closing_message = '';
  if (opening && typeof opening === 'object') {
    const o = opening as Record<string, unknown>;
    opening_purpose = typeof o.purpose === 'string' ? o.purpose : '';
    opening_instructions = typeof o.instructions === 'string' ? o.instructions : '';
  }
  if (closing && typeof closing === 'object') {
    const c = closing as Record<string, unknown>;
    closing_message = typeof c.message === 'string' ? c.message : '';
  }
  return { opening_purpose, opening_instructions, closing_message };
}

/** Empty messaging shell — seed from template via messagingFromTemplate. */
export function emptyMessagingSetup(assessmentName = ''): CampaignMessagingSetupValue {
  return {
    invite_message: '',
    opening_purpose: '',
    opening_instructions: '',
    closing_message: '',
    reminder_message: defaultCampaignReminderMessage(assessmentName),
  };
}

/**
 * Copy template respondent framing into campaign messaging.
 * Invite body defaults to opening purpose when present.
 */
export function messagingFromTemplate(
  framing: TemplateRespondentFramingDefaults,
  templatePurpose = '',
  assessmentName = ''
): CampaignMessagingSetupValue {
  const purpose = framing.purpose.trim() || templatePurpose.trim();
  return {
    opening_purpose: framing.purpose.trim() || purpose,
    opening_instructions: framing.instructions,
    closing_message: framing.closingMessage,
    invite_message: purpose,
    reminder_message: defaultCampaignReminderMessage(assessmentName),
  };
}

/**
 * Fill blank campaign fields from template (legacy campaigns that still pointed at template).
 * Does not overwrite non-empty campaign text.
 */
export function hydrateMessagingFromTemplate(
  current: CampaignMessagingSetupValue,
  framing: TemplateRespondentFramingDefaults,
  templatePurpose = '',
  assessmentName = ''
): CampaignMessagingSetupValue {
  const seeded = messagingFromTemplate(framing, templatePurpose, assessmentName);
  const opening_purpose = current.opening_purpose.trim() || seeded.opening_purpose;
  return {
    opening_purpose,
    opening_instructions: current.opening_instructions.trim() || seeded.opening_instructions,
    closing_message: current.closing_message.trim() || seeded.closing_message,
    invite_message: opening_purpose,
    reminder_message:
      current.reminder_message.trim() ||
      seeded.reminder_message ||
      defaultCampaignReminderMessage(assessmentName),
  };
}

export function parseMessagingSetup(
  notificationConfig: Record<string, unknown> | null | undefined,
  assessmentName = ''
): CampaignMessagingSetupValue {
  const cfg = notificationConfig ?? {};
  const framingOverride = parseRespondentFramingOverride(cfg);
  const inviteFromConfig =
    typeof cfg.invite_message === 'string' ? cfg.invite_message : '';
  // Prefer stored opening purpose; fall back to invite_message for older rows.
  const opening_purpose = framingOverride.opening_purpose.trim() || inviteFromConfig;
  const reminderFromConfig =
    typeof cfg.reminder_message === 'string' ? cfg.reminder_message.trim() : '';
  return {
    invite_message: opening_purpose,
    opening_purpose,
    opening_instructions: framingOverride.opening_instructions,
    closing_message: framingOverride.closing_message,
    reminder_message: reminderFromConfig || defaultCampaignReminderMessage(assessmentName),
  };
}

export function campaignToSetupState(campaign: AeCampaign): CampaignSetupFormState {
  const perPeriod = campaignResponsePeriodMode(campaign) === 'per_period';
  const granularity = (campaignPeriodGranularity(campaign.time_period_config) ??
    'quarter') as PeriodGranularity;
  const notificationConfig = (campaign.notification_config ?? {}) as Record<string, unknown>;

  const digest = parseAdminCompletionDigest(notificationConfig);

  return {
    form: {
      template_id: campaign.template_id,
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      distribution_mode: campaign.distribution_mode,
      is_anonymous: campaign.is_anonymous,
      captcha_required: false,
      email_verification_required: campaign.email_verification_required !== false,
      branding: (campaign.branding as Record<string, unknown>) ?? {},
      report_defaults: {
        show_instant_individual_results: true,
        benchmark_b1_min_responses: 10,
        ...((campaign.report_defaults as Record<string, unknown>) ?? {}),
      },
      presentation_style: (campaign.presentation_style ?? 'question') as AePresentationStyle,
      respondent_config: {
        ...AE_DEFAULT_RESPONDENT_CONFIG,
        ...(campaign.respondent_config as typeof AE_DEFAULT_RESPONDENT_CONFIG),
      },
    },
    responsesPerPeriod: perPeriod,
    periodGranularity: granularity,
    remindOnNewPeriod: notificationConfig.remind_on_new_period === true,
    messagingSetup: parseMessagingSetup(notificationConfig, campaign.name),
    pendingInviteEmails: '',
    adminCompletionDigest: digest.frequency,
    adminCompletionDigestLastSentAt: digest.last_sent_at,
  };
}

export function buildCampaignSetupPayload(state: CampaignSetupFormState): CreateCampaignInput {
  const reportDefaults = (state.form.report_defaults ?? {}) as Record<string, boolean | number>;
  const ms = state.messagingSetup;
  const notificationConfig: Record<string, unknown> = {
    // Campaign owns copy going forward (legacy flags cleared on save).
    invite_use_template_purpose: false,
    // Invite email body = opening purpose (kept in sync).
    invite_message: ms.opening_purpose.trim() || null,
    // Reminder email body (Respondent experience).
    reminder_message: ms.reminder_message.trim() || null,
    use_template_opening_purpose: false,
    use_template_opening_instructions: false,
    use_template_closing_message: false,
    respondent_framing: {
      opening: {
        purpose: ms.opening_purpose.trim() || null,
        instructions: ms.opening_instructions.trim() || null,
      },
      closing: {
        message: ms.closing_message.trim() || null,
      },
    },
  };

  if (state.remindOnNewPeriod) {
    notificationConfig.remind_on_new_period = true;
  }

  notificationConfig.admin_completion_digest = {
    frequency: state.adminCompletionDigest,
    ...(state.adminCompletionDigestLastSentAt
      ? { last_sent_at: state.adminCompletionDigestLastSentAt }
      : {}),
  };

  const respondentConfig = { ...state.form.respondent_config } as AeRespondentConfig;
  if (state.form.distribution_mode === 'invite_only' && respondentConfig?.completion_options) {
    respondentConfig.completion_options = {
      ...respondentConfig.completion_options,
      allow_share_link: false,
    };
  }

  return {
    ...state.form,
    captcha_required: false,
    email_verification_required: state.form.email_verification_required !== false,
    name: state.form.name.trim(),
    campaign_type: state.responsesPerPeriod ? 'evergreen' : 'one_off',
    time_period_config: state.responsesPerPeriod
      ? { granularity: state.periodGranularity, timezone: 'UTC' }
      : null,
    notification_config: notificationConfig,
    report_defaults: reportDefaults,
    respondent_config: respondentConfig,
  };
}

/** Messaging-only patch for open/closed campaigns (notification_config). */
export function buildCampaignMessagingPayload(
  state: CampaignSetupFormState
): Pick<CreateCampaignInput, 'notification_config'> {
  return { notification_config: buildCampaignSetupPayload(state).notification_config };
}

export function buildCampaignEditSubmitPayload(
  state: CampaignSetupFormState,
  options: {
    readOnly: boolean;
    messagingEditable: boolean;
    benchmarkEditable: boolean;
  }
): CreateCampaignInput | Partial<CreateCampaignInput> {
  const reportDefaults = (state.form.report_defaults ?? {}) as Record<string, boolean | number>;
  if (!options.readOnly) {
    return buildCampaignSetupPayload(state);
  }
  const payload: Partial<CreateCampaignInput> = {};
  if (state.form.name.trim()) {
    payload.name = state.form.name.trim();
  }
  if (options.messagingEditable) {
    Object.assign(payload, buildCampaignMessagingPayload(state));
    // Respondent experience: email OTP gate stays editable after open (not archived).
    payload.email_verification_required = state.form.email_verification_required !== false;
  }
  if (options.benchmarkEditable) {
    payload.report_defaults = {
      benchmark_b1_min_responses: Math.max(1, Number(reportDefaults.benchmark_b1_min_responses ?? 10)),
    };
  }
  return payload;
}

/** Stable fingerprint for debounced auto-save (edit hub). */
export function campaignSetupStateFingerprint(state: CampaignSetupFormState): string {
  return JSON.stringify(buildCampaignSetupPayload(state));
}

export function parseInviteEmailList(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}
